import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Lightbulb, Shield, Award, Handshake, Zap, Clock, ShieldCheck, ClipboardCheck, Users } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { CTABanner } from '../components/CTABanner';
export function AboutPage() {
  const values = [{
    icon: <Lightbulb data-id="element-170" />,
    title: 'Innovation',
    desc: 'We leverage our creativity to deliver practical, innovative solutions'
  }, {
    icon: <ShieldCheck data-id="element-171" />,
    title: 'Safety',
    desc: 'We integrate safety into everything we do'
  }, {
    icon: <ClipboardCheck data-id="element-172" />,
    title: 'Accountability',
    desc: 'We take responsibility for our actions and hold others accountable for theirs'
  }, {
    icon: <Handshake data-id="element-173" />,
    title: 'Service',
    desc: 'We build sustainable relationships through customer engagement'
  }, {
    icon: <Shield data-id="element-174" />,
    title: 'Integrity',
    desc: 'We are fair and honest, open and straightforward both within the company and with customers'
  }, {
    icon: <Users data-id="element-175" />,
    title: 'Teamwork',
    desc: 'We work together as one team and with others to achieve results'
  }];
  const team = [{
    name: 'Adesola Soetan',
    title: 'MD/CEO/Director',
    initials: 'AS',
    color: 'from-blue-500 to-blue-700',
    bio: "Adesola has a B.Sc. in International Relations from Obafemi Awolowo University (O.A.U), Ile-Ife, Osun State, Nigeria. He is an Oracle Certified Associate, holds an Executive Masters Certificate from the Project Management College UK, and is a certified Lead Information Security Auditor from the British Standard Institute in Dubai. He is an experienced Project Manager with over 10 years' experience managing projects that aggregate to over 15 billion Naira across the Information and Communications Technology Spectrum. His journey with Verreaux Technologies commenced in 2009 while managing the Nation-Wide Rollout of the School Access Program. In 2010, Adesola joined Verreaux Technologies as its Head of Operations and has been with the sister company Hotspot Networks since then."
  }, {
    name: 'Ayodeji Soetan',
    title: 'Director',
    initials: 'AY',
    color: 'from-green-500 to-green-700',
    bio: "Ayodeji Soetan is a business process implementation specialist and currently a Business Systems Analyst with Canada's top airline, Air Canada. He has been with the airline for 3 years and championed various operational and strategic restructuring projects which helped to save, generate and enhance financial services. He previously worked with Investors Group, a subsidiary of Power Corp. International in Canada. He has applied his business process implementation skills in the Middle East, United Kingdom, and his birth country Nigeria. Ayo holds a Master's degree in Managing Information Technology from the University of Salford in Manchester, United Kingdom."
  }, {
    name: 'Engr. Morenikeji Aniye',
    title: 'Director',
    initials: 'MA',
    color: 'from-orange-500 to-orange-700',
    bio: "Engr. Morenikeji Aniye is the Chief Executive Officer of Hotspot Network Limited and the Founder of Hotspot Network Limited. A dynamic and talented entrepreneur with many years of experience working at top management level in the major telecom service providers in Nigeria. He has over a decade of experience in the Telecommunications industry, highly experienced in Radio Network Planning and Optimization, and strategy formulation and cost-effective solutions. He is a member of COREN, Fellow of the Nigerian Society of Engineers, Project Management Institute, and Associate Member of the Nigerian Institute of Management. He led the decommissioning and cutover of BTS Infrastructure from Motorola to Ericsson in the nation's capital, Abuja."
  }];
  return <main className="w-full pt-20" data-id="element-176">
      {/* Page Header */}
      <section className="bg-navy-900 py-20 relative overflow-hidden" data-id="element-177">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-178" />
        <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-179">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6" data-id="element-180">
            About <span className="text-tech-green" data-id="element-181">Invigo</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-slate-300 max-w-2xl" data-id="element-182">
            Pioneering digital transformation and building the technological
            backbone of Africa's future.
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-white" data-id="element-183">
        <div className="max-w-7xl mx-auto px-4" data-id="element-184">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-185">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-186">
              <SectionHeading title="Our Evolution" data-id="element-187" />
              <div className="space-y-6 text-slate-600 leading-relaxed text-lg" data-id="element-188">
                <p data-id="element-189">
                  Invigo Technology is a fully rebranded and enhanced company,
                  formally known as Inspired Delta Technology Solutions. Our
                  mission to become the leading brand name in ICT
                  Infrastructure, design, build and managed services in
                  Sub-Saharan Africa remains unchanged and much stronger.
                </p>
                <p data-id="element-190">
                  The company's technology independence, global talent and
                  extensive partner network help clients harness the power of
                  innovation to thrive on change and guide their digital
                  transformation journeys.
                </p>
                <p data-id="element-191">
                  We work to create greater value for our clients, partners and
                  shareholders, and to present growth opportunities for its
                  people.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative" data-id="element-192">
              <div className="aspect-square rounded-full bg-slate-50 absolute -top-10 -right-10 w-full z-0" data-id="element-193" />
              <div className="relative z-10 bg-navy-900 rounded-3xl p-10 overflow-hidden shadow-2xl" data-id="element-194">
                <div className="absolute top-0 right-0 w-64 h-64 bg-tech-green/20 blur-[80px] rounded-full" data-id="element-195" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-tech-blue/20 blur-[80px] rounded-full" data-id="element-196" />

                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center py-10" data-id="element-197">
                  <div className="text-slate-400 mb-4 font-medium tracking-widest uppercase text-sm" data-id="element-198">
                    From
                  </div>
                  <div className="text-2xl font-bold text-slate-300 mb-8 opacity-50" data-id="element-199">
                    Inspired Delta
                  </div>
                  <div className="w-px h-16 bg-gradient-to-b from-slate-600 to-tech-green mb-8" data-id="element-200" />
                  <div className="text-slate-400 mb-4 font-medium tracking-widest uppercase text-sm" data-id="element-201">
                    To
                  </div>
                  <div className="text-4xl font-extrabold text-white" data-id="element-202">
                    Invigo<span className="text-tech-green" data-id="element-203">Tech</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-slate-50" data-id="element-204">
        <div className="max-w-7xl mx-auto px-4" data-id="element-205">
          <div className="grid md:grid-cols-2 gap-8" data-id="element-206">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-10 rounded-3xl shadow-sm border border-slate-100" data-id="element-207">
              <div className="w-16 h-16 rounded-2xl bg-tech-blue/10 flex items-center justify-center text-tech-blue mb-8" data-id="element-208">
                <Eye className="w-8 h-8" data-id="element-209" />
              </div>
              <h3 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-210">
                Our Vision
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed" data-id="element-211">
                To become a leading brand name in ICT infrastructure design,
                build and manage services/solutions in Nigeria and sub-Saharan
                Africa in the current decade.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-navy-900 p-10 rounded-3xl shadow-xl" data-id="element-212">
              <div className="w-16 h-16 rounded-2xl bg-tech-green/20 flex items-center justify-center text-tech-green mb-8" data-id="element-213">
                <Target className="w-8 h-8" data-id="element-214" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-215">
                Our Mission
              </h3>
              <p className="text-lg text-slate-300 leading-relaxed" data-id="element-216">
                Invigo Technology will design, build and support ICT services
                using FITE to become a reference point or an industry benchmark.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white" data-id="element-217">
        <div className="max-w-7xl mx-auto px-4" data-id="element-218">
          <SectionHeading title="Our Core Values" centered data-id="element-219" />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16" data-id="element-220">
            {values.map((value, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="p-8 rounded-2xl border border-slate-100 hover:border-tech-blue/30 hover:shadow-lg transition-all group" data-id="element-221">
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-navy-900 mb-6 group-hover:bg-tech-blue group-hover:text-white transition-colors" data-id="element-222">
                  {value.icon}
                </div>
                <h4 className="text-xl font-bold text-navy-900 mb-3" data-id="element-223">
                  {value.title}
                </h4>
                <p className="text-slate-600" data-id="element-224">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-24 bg-slate-50" data-id="element-225">
        <div className="max-w-7xl mx-auto px-4" data-id="element-226">
          <SectionHeading title="Leadership Team" subtitle="Guided by experienced professionals dedicated to technological excellence." centered data-id="element-227" />

          <div className="grid gap-8 mt-16" data-id="element-228">
            {team.map((member, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row gap-8 items-start border border-slate-100" data-id="element-229">
                <div className={`w-32 h-32 shrink-0 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center text-white text-4xl font-bold shadow-lg`} data-id="element-230">
                  {member.initials}
                </div>
                <div data-id="element-231">
                  <h4 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-232">
                    {member.name}
                  </h4>
                  <p className="text-tech-blue font-semibold text-sm mb-6 uppercase tracking-wider" data-id="element-233">
                    {member.title}
                  </p>
                  <p className="text-slate-600 leading-relaxed" data-id="element-234">{member.bio}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <CTABanner data-id="element-235" />
    </main>;
}